import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Names, Values } from '@mode-switch/model';
import { BasicChannel, EncField, Encoding, EncodingResult } from '@mode-vegas/vegas';
import { AccountFacade } from '@mode/accounts/contract';
import { ChartFacade, DragFieldInfo, SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers, RenderDataModel, buildDefaultContinuousFieldColor } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledField } from '@mode/shared/util-switch';
import { PillActionType } from 'libs/shared/contract-common/src/lib/charts';
import { Observable, Subject, of } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { DragEndData, DragStartData, FieldDropData, OnDropEvent } from '../../helpers/field-helpers';
import { ChannelSummary } from '../../marks-card/marks-helpers';

@Component({
  selector: 'mode-pivot-builder',
  templateUrl: './pivot-builder.component.html',
  styleUrls: ['./pivot-builder.component.scss'],
})
export class PivotBuilderComponent implements OnInit, OnDestroy, OnChanges {
  @Input({ required: true }) encoding!: Encoding;
  @Input() observables: any;
  @Input() chartService!: SelectedChartService;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input() isExploring = false;

  queryToken$: Observable<string> | undefined;
  view$: Observable<FlamingoTypes.ModeView> | undefined;
  vizToken$: Observable<string> | undefined;
  fieldFormatMap$: Observable<ChartTypes.FieldFormatMap> | undefined;
  fieldColorMap$: Observable<ChartTypes.FieldColorMap> | undefined;
  chartType$: Observable<ChartTypes.ChartType> | undefined;

  view: FlamingoTypes.ModeView | undefined;
  allColorPalettes: OrgTypes.ColorPalette[] = [];
  fieldColorMap: ChartTypes.FieldColorMap | undefined;

  dragInfo: DragFieldInfo | undefined;
  isDragValidForValues = false;

  columns: ReadonlyArray<EncField> = [];
  rows: ReadonlyArray<EncField> = [];
  namesChannel: BasicChannel = 'row';

  isChartEncodingComplete = false;
  hasMeasureValues = false;
  chartTypeVegasPivotTable: ChartTypes.ChartType = 'vegasPivotTable';

  colorSummary: ChannelSummary | undefined;
  renderModel$: Observable<RenderDataModel> | undefined;
  queryRunToken$: Observable<string> | undefined;
  isAutoOrManualRender$: Observable<boolean> | undefined;
  format$: Observable<ChartTypes.ChartFormat> | undefined;
  binSizeToFieldMap$: Observable<BinSizeTypes.BinSizeToFieldMap> | undefined;

  fieldInColor = false;
  allValuesFieldInColor = false;

  private destroyed$ = new Subject<void>();

  constructor(
    private chartFacade: ChartFacade,
    private accountFacade: AccountFacade,
    private analyticsService: AnalyticsService,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone
  ) {
    this.accountFacade.reportOwnerColorPalettes$.pipe(takeUntil(this.destroyed$)).subscribe((palettes) => {
      this.allColorPalettes = [...palettes].sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  get isValues(): boolean | undefined {
    return this.dragInfo?.formula?.isValues;
  }

  ngOnInit(): void {
    this.queryToken$ = this.observables.queryToken$;
    this.queryRunToken$ = this.observables.queryRunToken$;
    this.view$ = this.observables.view$;
    this.vizToken$ = this.isExploring ? of(FLAMINGO_RESERVED_REFS.Exploration) : this.observables.token$;
    this.view$?.pipe(takeUntil(this.destroyed$)).subscribe((view) => (this.view = view));
    this.isAutoOrManualRender$ = this.observables.isAutoOrManualRender$;
    this.renderModel$ = this.observables.renderModel$;
    this.format$ = this.observables.format$;
    this.chartType$ = this.observables.chartType$;
    this.binSizeToFieldMap$ = this.observables?.binSizeToFieldMap$;

    this.fieldFormatMap$ = this.observables.fieldFormats$;
    this.fieldColorMap$ = this.observables.fieldColors$;

    this.chartFacade.dragField$.pipe(takeUntil(this.destroyed$)).subscribe((dragInfo: DragFieldInfo) => {
      this.dragInfo = dragInfo;
      this.isDragValidForValues = this.dragInfo ? EncodingHelpers.canAddOrMoveToValues(dragInfo.formula) : false;
    });

    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('dragover', this.documentDragOver);
    });

    this.namesChannel = this.getNamesChannel();
    this.isChartEncodingComplete = EncodingHelpers.isChartEncodingComplete(
      this.encoding,
      this.chartTypeVegasPivotTable
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.document.removeEventListener('dragover', this.documentDragOver);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['observables']) {
      this.queryToken$ = this.observables.queryToken$;
      this.queryRunToken$ = this.observables.queryRunToken$;
      this.view$ = this.observables.view$;
      this.view$?.pipe(takeUntil(this.destroyed$)).subscribe((view) => (this.view = view));

      this.vizToken$ = this.isExploring ? of(FLAMINGO_RESERVED_REFS.Exploration) : this.observables.token$;
      this.fieldFormatMap$ = this.observables.fieldFormats$;
      this.fieldColorMap$ = this.observables.fieldColors$;
      this.format$ = this.observables.format$;
      this.isAutoOrManualRender$ = this.observables.isAutoOrManualRender$;
      this.renderModel$ = this.observables.renderModel$;
      this.fieldColorMap$
        ?.pipe(takeUntil(this.destroyed$))
        .subscribe((fieldColorMap) => (this.fieldColorMap = fieldColorMap));
    }

    this.isChartEncodingComplete = EncodingHelpers.isChartEncodingComplete(
      this.encoding,
      this.chartTypeVegasPivotTable
    );
    this.hasMeasureValues = EncodingHelpers.isInEncoding(this.encoding, Values.Key);
    this.columns = this.buildColumns();
    this.rows = this.buildRows();
    //make sure we have an encoding change before getting namesChannel from it
    if (changes.encoding) {
      this.namesChannel = this.getNamesChannel();
    }
    const colorField = this.encoding.marks.properties.color.values[0];
    const summaryMark: ChannelSummary = {
      channel: 'color',
      type: colorField != null ? 'field' : 'empty',
      field: colorField,
      id: colorField?.id,
      fieldIds: colorField?.id != null ? [colorField.id] : [],
      isValid: true,
    };
    this.colorSummary = summaryMark;
    const allColorFields = EncodingHelpers.getUniqueEncFieldsByMarkChannel(this.encoding, 'color');
    this.fieldInColor = allColorFields.length > 0;
    this.allValuesFieldInColor = allColorFields.some((field) => field.formula === Values.Key);
  }

  documentDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onFieldDrop({ field, channel }: FieldDropData) {
    if (this.dragInfo == null) {
      return;
    }
    const formula = this.dragInfo.formula;
    const fieldIds = this.dragInfo.fields;
    this.chartFacade.endFieldDrag();

    let newEncoding;

    if (formula == null || field.id == null) {
      return;
    }

    if (formula.isField) {
      if (formula.alias === field.id) {
        // inserting field in the same location as it was
        return;
      }
      // move field and dispatch
      newEncoding = EncodingHelpers.replaceAndMoveVegasField(
        this.encoding,
        fieldIds ? fieldIds : (formula.alias ?? ''),
        channel,
        field.id
      );
    } else {
      newEncoding = EncodingHelpers.replaceAndAddNewVegasField(this.encoding, formula, field.id, channel);
    }

    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  onDrop($event: OnDropEvent) {
    if (this.dragInfo == null) {
      return;
    }
    const { event, channel, attr } = $event;
    const formula = attr ? attr : this.dragInfo?.formula;
    const fieldIds = this.dragInfo?.fields;
    const encoding = this.encoding;
    const view = this.view;
    let zoneType: EncodingHelpers.ZoneType | undefined;

    this.chartFacade.endFieldDrag();

    if (view == null) {
      return;
    }

    // Validate target channel
    switch (channel) {
      case 'column':
      case 'row':
        //column and row are always discrete in pivot table
        zoneType = EncodingHelpers.ZoneType.Discrete;
        break;
      case 'value': {
        break;
      }
      // Bail if it's not a channel
      // we need to handle drops for
      default: {
        return;
      }
    }

    const fieldList = channel === 'column' ? this.columns : this.rows;
    const dropIndex = channel === 'value' ? event.index : Math.min(event.index ?? 0, fieldList.length);
    const newEncoding = EncodingHelpers.addOrMoveVegasFieldToBasicChannel(
      view,
      encoding,
      channel,
      formula,
      dropIndex,
      fieldIds,
      zoneType
    );

    if (newEncoding != null) {
      this.chartService.dispatchEncodingAndFormatUpdate(
        EncodingHelpers.updateAutomaticMarkType(view, encoding, newEncoding),
        channel === 'column' || channel === 'row' ? {} : undefined
      );
    }
  }

  onDragStart({ field, source }: DragStartData) {
    if (this.view && [...this.columns, ...this.rows, ...this.encoding.value].some((f) => f.id === field.id)) {
      const compiledField = CompiledField.fromEncField(this.view, field as EncField);
      this.chartFacade.startFieldDrag(compiledField, source);
    }
  }

  onDragEnd({ field }: DragEndData) {
    if (this.dragInfo == null) {
      return;
    }
    if (this.dragInfo.formula) {
      this.removeField(
        { $field: field },
        this.dragInfo.source === 'column' || this.dragInfo.source === 'row' ? {} : undefined
      );
      this.chartFacade.endFieldDrag();
    }
  }

  isDragging() {
    if (this.dragInfo?.formula?.isSystem) {
      return false;
    }

    return !!this.dragInfo?.formula;
  }

  fieldUpdated($event: { $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }) {
    const { $result, $formats } = $event;
    if ($formats != null) {
      this.observables.fieldFormats$.pipe(first()).subscribe((fieldFormats: ChartTypes.FieldFormatMap) => {
        // Merge the partial format update with the existing FieldFormatMap. The update in chart service
        // does a replace.
        this.chartService.dispatchEncodingAndFormatUpdate($result, undefined, { ...(fieldFormats ?? {}), ...$formats });
      });
    } else {
      this.chartService.dispatchEncodingUpdate($result, PillActionType.ChangePillFormula);
    }
  }

  removeField($event: { $field: EncField }, facetSizes?: ChartTypes.FacetSizeMap) {
    const { $field } = $event;
    if ($field.id == null) {
      return;
    }
    let encResult = EncodingHelpers.removeFieldFromEncoding(this.encoding, this.chartTypeVegasPivotTable, $field.id); // can use any chart type in the shape

    // Update the automatic mark type and the facet size map if needed and dispatch the update event.
    // "pipe(first()).subscribe()" executes the subscription callback once, then removes the subscription.
    this.view$?.pipe(first()).subscribe((view) => {
      encResult = EncodingHelpers.updateAutomaticMarkType(view, this.encoding, encResult);
      this.chartService.dispatchEncodingAndFormatUpdate(encResult, facetSizes);
    });
  }

  removeSort($event: { $source: string }) {
    const { $source } = $event;
    this.chartService.removeSort($source);
  }

  getBinSize($event: { columnName: string; token: string }) {
    this.chartService.getBinSize($event.token, $event.columnName);
  }

  isValuesEmpty(): boolean {
    return this.encoding?.value.length === 0;
  }

  filterFields(fields: ReadonlyArray<EncField>): Array<EncField> {
    return fields.filter((field) => field.formula !== Names.Key);
  }

  buildColumns() {
    if (this.encoding != null) {
      const fields = EncodingHelpers.buildBasicColumns(this.encoding);
      return this.filterFields(fields);
    }
    return [];
  }

  buildRows() {
    if (this.encoding != null) {
      const fields = EncodingHelpers.buildBasicRows(this.encoding);
      return this.filterFields(fields);
    }
    return [];
  }
  /**
   * get the nameChannel var with what we find in the current encoding
   */
  getNamesChannel() {
    return this.encoding.x.find((f) => f.formula === Names.Key) ? 'column' : 'row';
  }

  /**
   * @description Handler for row/col cta in PTQC values dropzone
   */
  onSwitchNamesAxes() {
    const columnsAndRows = [...this.encoding.x, ...this.encoding.y, ...this.encoding.row, ...this.encoding.column];
    const namesField = columnsAndRows.find((f) => f.formula === Names.Key);
    const newEnc = EncodingHelpers.swapRowColAxes(this.encoding, namesField?.id || '');
    this.chartService.dispatchEncodingUpdate(newEnc);
    this.namesChannel = this.namesChannel === 'row' ? 'column' : 'row';
  }

  addAllValuesToColor() {
    this.renderModel$?.pipe(first()).subscribe((renderDataModel) => {
      const newEncoding = EncodingHelpers.addMeasureValuesToColorChannel(this.encoding);
      const defaultFieldColor = buildDefaultContinuousFieldColor(
        newEncoding.encoding,
        renderDataModel,
        undefined,
        this.fieldColorMap,
        this.allColorPalettes
      );
      const fieldColors = { ...this.fieldColorMap, ...(defaultFieldColor ?? {}) };
      this.chartService.dispatchEncodingAndFormatUpdate(newEncoding, undefined, undefined, undefined, fieldColors);

      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        location: AnalyticsEvent.SubContext.Chart,
        action: 'PTQC colors add all',
        resource: AnalyticsEvent.Context.Visualization,
      });
    });
  }

  clearAllColors() {
    const encResult = EncodingHelpers.removeAllColorsFromEncoding(this.encoding);
    if (encResult) {
      this.chartService.dispatchEncodingUpdate(encResult);
    }
  }
}
